/**
 * PlotShareViewer — FXC-8724
 *
 * Full-screen, mobile-first, read-only viewer for shared plots.
 * Opened via /plot-share/:token (QR code, link share).
 *
 * Layout (mobile-first):
 *   ┌────────────────────────────┐
 *   │   header bar (title only)  │ 48px
 *   ├────────────────────────────┤
 *   │         PLOT               │ flex-grow (fills remaining height)
 *   ├────────────────────────────┤
 *   │  flowfield panel (toggle)  │ 180px when open
 *   ├────────────────────────────┤
 *   │    chat panel (slide-up)   │ 50vh when open
 *   └────────────────────────────┘
 *
 * On wider viewports (≥ 768 px) the flowfield and chat panels sit
 * side-by-side in a right column while the plot takes the left.
 *
 * Graceful degradation:
 * - Flowfield 501/503 → "Flowfield loading" notice (no error toast)
 * - Chat 501/503       → "Chat unavailable" notice (no error toast)
 * - plotData null      → skeleton/spinner (parent handles loading)
 *
 * Consumes: usePlotShareContext() — provided by PlotShareView (FXC-8720)
 * Requires: ProvenanceProvider stub so PlotlyRenderer's internal hook works
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Spinner } from "@radix-ui/themes";

import { usePlotShareContext } from "../../pages/PlotShareView";
import { PlotlyRenderer } from "../../components/plot-builder/renderers/PlotlyRenderer";
import {
  type PlotBuilderConfig,
  createDefaultConfig,
} from "../../components/plot-builder/types";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import { unauthenticatedApiClient } from "../../api/client";

// ---------------------------------------------------------------------------
// Flowfield types (FXC-8721)
// ---------------------------------------------------------------------------

interface FlowfieldPoint {
  id: string;
  label?: string;
  coords?: Record<string, number>;
}

interface FlowfieldResponse {
  // Structured per-point view (deferred follow-up). When absent, the panel
  // degrades to "not yet available" — FXC-8721 currently serves the raw
  // cached UVF bundle (files/source) instead.
  points?: FlowfieldPoint[];
  vectors?: Record<string, unknown>[];
  files?: Record<string, string>;
  source?: string;
}

// ---------------------------------------------------------------------------
// Chat types (FXC-8722)
// ---------------------------------------------------------------------------

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  ts: number;
}

// ---------------------------------------------------------------------------
// Derived plot config from SharedPlotDataResponse
// ---------------------------------------------------------------------------

function derivePlotConfig(plot: Record<string, unknown>): PlotBuilderConfig {
  const base = createDefaultConfig();
  const config = (plot.config ?? {}) as Record<string, unknown>;
  const chartType = (plot.chart_type as PlotBuilderConfig["chartType"]) ?? base.chartType;
  const x = (config.xAxis as Record<string, string> | undefined)?.field ?? base.xAxis.field;
  const y = (config.yAxis as Record<string, string> | undefined)?.field ?? base.yAxis.field;
  const titleStr = (plot.title as string | undefined) ?? base.title;

  return {
    ...base,
    ...(config as Partial<PlotBuilderConfig>),
    chartType,
    title: titleStr,
    xAxis: {
      ...base.xAxis,
      ...(config.xAxis as Partial<PlotBuilderConfig["xAxis"]> ?? {}),
      field: x,
    },
    yAxis: {
      ...base.yAxis,
      ...(config.yAxis as Partial<PlotBuilderConfig["yAxis"]> ?? {}),
      field: y,
    },
  };
}

// ---------------------------------------------------------------------------
// Flowfield panel
// ---------------------------------------------------------------------------

interface FlowfieldPanelProps {
  plotId: string;
  token: string;
  allowedPoints: string[] | null;
}

function FlowfieldPanel({ plotId, token, allowedPoints }: FlowfieldPanelProps) {
  const [status, setStatus] = useState<"loading" | "unavailable" | "ready" | "error">("loading");
  const [data, setData] = useState<FlowfieldResponse | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    unauthenticatedApiClient<FlowfieldResponse>(
      `/plots/share/${plotId}/flowfield?token=${encodeURIComponent(token)}`,
    )
      .then((res) => {
        if (cancelled) return;
        // FXC-8721 serves a cached UVF bundle as { files, source, ... }. Structured
        // per-point vectors ({ points: [...] }) are a deferred follow-up — until the
        // bundle is parsed into points, degrade to the "not yet available" notice
        // rather than crash on an absent points array.
        if (res && Array.isArray(res.points)) {
          setData(res);
          setStatus("ready");
        } else {
          setStatus("unavailable");
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        // 428 Precondition Required — pre-bake not run yet (FXC-8721)
        // 501 Not Implemented / 503 Service Unavailable — backend not wired yet
        if (/428|501|503|not implemented|not available|precondition/i.test(msg)) {
          setStatus("unavailable");
        } else {
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [plotId, token]);

  const visiblePoints = data?.points?.filter(
    (p) => !allowedPoints || allowedPoints.includes(p.id),
  ) ?? [];

  if (status === "loading") {
    return (
      <div style={styles.ffLoading} aria-label="Flowfield loading">
        <Spinner size="1" />
        <span style={{ fontSize: 12, color: "var(--gray-9)", marginLeft: 6 }}>
          Flowfield loading…
        </span>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div style={styles.ffUnavailable} aria-label="Flowfield unavailable">
        <span style={{ fontSize: 11, color: "var(--gray-8)" }}>
          Flowfield not yet available
        </span>
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div style={styles.ffUnavailable} aria-label="Flowfield error">
        <span style={{ fontSize: 11, color: "var(--red-9)" }}>
          Could not load flowfield
        </span>
      </div>
    );
  }

  return (
    <div style={styles.ffReady} aria-label="Flowfield points">
      <div style={styles.ffTitle}>Flowfield points</div>
      <div style={styles.ffPointList}>
        {visiblePoints.length === 0 ? (
          <span style={{ fontSize: 11, color: "var(--gray-8)" }}>
            No points available
          </span>
        ) : (
          visiblePoints.map((pt) => (
            <button
              key={pt.id}
              onClick={() => setSelected(selected === pt.id ? null : pt.id)}
              style={{
                ...styles.ffPoint,
                background: selected === pt.id
                  ? "var(--accent-4, #E6F4ED)"
                  : "var(--gray-2, #F8F9F8)",
                border: `1px solid ${selected === pt.id ? "var(--accent-9, #00643C)" : "var(--gray-5, #D0D5D2)"}`,
                color: selected === pt.id ? "var(--accent-11, #003D25)" : "var(--gray-11, #111813)",
              }}
              aria-pressed={selected === pt.id}
            >
              {pt.label ?? pt.id}
            </button>
          ))
        )}
      </div>
      {selected && data.vectors && (
        <div style={styles.ffVectorInfo} aria-label="Flowfield vector data">
          <span style={{ fontSize: 10, color: "var(--gray-8)" }}>
            Point: <strong>{selected}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat panel
// ---------------------------------------------------------------------------

interface ChatPanelProps {
  plotId: string;
  token: string;
}

function ChatPanel({ plotId, token }: ChatPanelProps) {
  const [status, setStatus] = useState<"loading" | "unavailable" | "ready" | "error">("loading");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Probe the chat endpoint for availability
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    // FXC-8722 probe shape: { plot_id, chat_available, model, post_url, scope }.
    // chat_available is false when OPENAI_API_KEY is unset — treat as unavailable.
    unauthenticatedApiClient<{ chat_available?: boolean }>(
      `/plots/share/${plotId}/chat?token=${encodeURIComponent(token)}`,
    )
      .then((res) => {
        if (!cancelled) setStatus(res?.chat_available === false ? "unavailable" : "ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (/501|503|not implemented|not available/i.test(msg)) {
          setStatus("unavailable");
        } else {
          // Other errors (404 on GET means it might accept POST) — allow input
          setStatus("ready");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [plotId, token]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = { role: "user", content: text, ts: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      // FXC-8722 contract: POST body { question }, response { answer, model, ... }.
      const res = await unauthenticatedApiClient<{ answer: string }>(
        `/plots/share/${plotId}/chat?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          body: { question: text },
        },
      );
      const assistantMsg: ChatMessage = {
        role: "assistant",
        content: res.answer ?? "(no reply)",
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (/501|503|not implemented|chat_unavailable/i.test(msg)) {
        setStatus("unavailable");
      } else if (/429|rate limit|too many/i.test(msg)) {
        const errMsg: ChatMessage = {
          role: "assistant",
          content: "You've reached the question limit for now — try again in a little while.",
          ts: Date.now(),
        };
        setMessages((prev) => [...prev, errMsg]);
      } else {
        const errMsg: ChatMessage = {
          role: "assistant",
          content: "Chat is temporarily unavailable.",
          ts: Date.now(),
        };
        setMessages((prev) => [...prev, errMsg]);
      }
    } finally {
      setSending(false);
    }
  }, [input, sending, plotId, token]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void sendMessage();
      }
    },
    [sendMessage],
  );

  if (status === "loading") {
    return (
      <div style={styles.chatLoading} aria-label="Chat loading">
        <Spinner size="1" />
        <span style={{ fontSize: 12, color: "var(--gray-9)", marginLeft: 6 }}>
          Chat loading…
        </span>
      </div>
    );
  }

  if (status === "unavailable") {
    return (
      <div style={styles.chatUnavailable} aria-label="Chat unavailable">
        <span style={{ fontSize: 12, color: "var(--gray-8)" }}>
          Q&A chat is not yet available for this plot
        </span>
      </div>
    );
  }

  return (
    <div style={styles.chatPanel} aria-label="Plot Q&A chat">
      {/* Message history */}
      <div ref={scrollRef} style={styles.chatMessages}>
        {messages.length === 0 && (
          <div style={styles.chatEmpty}>
            Ask a question about this plot…
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.ts}
            style={{
              ...styles.chatBubble,
              ...(msg.role === "user" ? styles.chatBubbleUser : styles.chatBubbleAssistant),
            }}
            aria-label={msg.role === "user" ? "Your message" : "Assistant response"}
          >
            {msg.content}
          </div>
        ))}
        {sending && (
          <div style={{ ...styles.chatBubble, ...styles.chatBubbleAssistant, display: "flex", alignItems: "center", gap: 6 }}>
            <Spinner size="1" />
            <span style={{ fontSize: 12 }}>Thinking…</span>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={styles.chatInputRow}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this plot…"
          disabled={sending}
          aria-label="Chat message input"
          style={styles.chatInput}
        />
        <button
          onClick={() => void sendMessage()}
          disabled={sending || !input.trim()}
          aria-label="Send message"
          style={{
            ...styles.chatSendBtn,
            opacity: sending || !input.trim() ? 0.5 : 1,
            cursor: sending || !input.trim() ? "not-allowed" : "pointer",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minimal ProvenanceProvider stub for the read-only share view
// PlotlyRenderer calls useProvenanceContext() — we satisfy it with no-ops
// and a synthetic projectId/branch from the share scope.
// ---------------------------------------------------------------------------

interface ShareProvenanceStubProps {
  projectId: string;
  branch: string;
  children: React.ReactNode;
}

function ShareProvenanceStub({ projectId, branch, children }: ShareProvenanceStubProps) {
  // ProvenanceProvider uses useSearchParams internally — we need a Router.
  // Since we're inside a real BrowserRouter (from App), we re-use it; but
  // the PlotlyRenderer only needs selectNodes/clearSelection (which we
  // provide as no-ops via the real provider). We just forward to the real
  // provider with the share scope values.
  return (
    <ProvenanceProvider projectId={projectId} branch={branch}>
      {children}
    </ProvenanceProvider>
  );
}

// ---------------------------------------------------------------------------
// Main viewer
// ---------------------------------------------------------------------------

export default function PlotShareViewer() {
  const { token, scope, plotData } = usePlotShareContext();

  const [showFlowfield, setShowFlowfield] = useState(false);
  const [showChat, setShowChat] = useState(false);

  if (!plotData || !scope) {
    // Parent (PlotShareView) handles the main loading/error. We're only called
    // when both token + data are resolved. If somehow undefined, show a brief
    // spinner.
    return (
      <div style={styles.fullscreen} aria-label="Loading plot viewer">
        <Spinner size="3" />
      </div>
    );
  }

  const { plot } = plotData;
  const config = derivePlotConfig(plot);
  const plotTitle = (plot.title as string | undefined) ?? "Shared Plot";
  const dataSnapshot = (plot.data_snapshot as Record<string, unknown>[]) ?? [];
  // Flowfield is an opt-in per share (default off) — hide the affordance unless
  // the token's scope grants it. The backend independently enforces this.
  const flowfieldAllowed = scope.flowfield_enabled === true;

  return (
    <ShareProvenanceStub projectId={scope.project_id} branch={scope.branch}>
      {/* Full-screen wrapper — no nav chrome */}
      <div style={styles.fullscreen}>
        {/* Compact header — title only */}
        <header style={styles.header}>
          <span style={styles.headerTitle} title={plotTitle}>
            {plotTitle}
          </span>
          <div style={styles.headerActions}>
            {flowfieldAllowed && (
              <button
                onClick={() => setShowFlowfield((v) => !v)}
                aria-pressed={showFlowfield}
                aria-label="Toggle flowfield panel"
                style={{
                  ...styles.headerBtn,
                  background: showFlowfield ? "var(--accent-4, #E6F4ED)" : "transparent",
                  color: showFlowfield ? "var(--accent-11, #003D25)" : "var(--gray-11, #4A524D)",
                }}
              >
                Field
              </button>
            )}
            <button
              onClick={() => setShowChat((v) => !v)}
              aria-pressed={showChat}
              aria-label="Toggle Q&A chat"
              style={{
                ...styles.headerBtn,
                background: showChat ? "var(--accent-4, #E6F4ED)" : "transparent",
                color: showChat ? "var(--accent-11, #003D25)" : "var(--gray-11, #4A524D)",
              }}
            >
              Chat
            </button>
          </div>
        </header>

        {/* Body: plot + optional side panels */}
        <div style={styles.body}>
          {/* Plot area */}
          <div style={styles.plotArea} aria-label="Plot viewer">
            {dataSnapshot.length === 0 ? (
              <div style={styles.noData}>No data available for this plot.</div>
            ) : (
              <PlotlyRenderer
                data={dataSnapshot}
                config={config}
                theme="dark"
                onPointClick={undefined}
                selectedNodeId={null}
              />
            )}
          </div>

          {/* Side panel (desktop: right column; mobile: bottom drawers) */}
          {((showFlowfield && flowfieldAllowed) || showChat) && (
            <div style={styles.sidePanel}>
              {showFlowfield && flowfieldAllowed && (
                <div style={styles.sidePanelSection} aria-label="Flowfield section">
                  <div style={styles.sidePanelSectionHeader}>
                    Flowfield
                    <button
                      onClick={() => setShowFlowfield(false)}
                      aria-label="Close flowfield"
                      style={styles.closePanelBtn}
                    >
                      ×
                    </button>
                  </div>
                  <FlowfieldPanel
                    plotId={scope.plot_id}
                    token={token}
                    allowedPoints={scope.allowed_points}
                  />
                </div>
              )}
              {showChat && (
                <div style={{ ...styles.sidePanelSection, flex: 1 }} aria-label="Chat section">
                  <div style={styles.sidePanelSectionHeader}>
                    Q&A
                    <button
                      onClick={() => setShowChat(false)}
                      aria-label="Close chat"
                      style={styles.closePanelBtn}
                    >
                      ×
                    </button>
                  </div>
                  <ChatPanel plotId={scope.plot_id} token={token} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </ShareProvenanceStub>
  );
}

// ---------------------------------------------------------------------------
// Styles — CSS-in-JS with --fc-* / --v5-* custom property references
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  fullscreen: {
    display: "flex",
    flexDirection: "column",
    width: "100vw",
    height: "100dvh", // dvh: dynamic viewport height (mobile safari)
    overflow: "hidden",
    background: "var(--gray-1, #161A18)",
    fontFamily: "var(--fc-font-family, system-ui, sans-serif)",
    color: "var(--gray-12, #F0F2F0)",
  },

  // Header bar
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 48,
    minHeight: 48,
    padding: "0 12px",
    borderBottom: "1px solid var(--gray-4, #2A302C)",
    background: "var(--gray-2, #1C211E)",
    flexShrink: 0,
    gap: 8,
    // Safe area inset for notch devices
    paddingTop: "env(safe-area-inset-top, 0px)",
  },

  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--gray-12, #F0F2F0)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    letterSpacing: "-0.01em",
  },

  headerActions: {
    display: "flex",
    gap: 4,
    flexShrink: 0,
  },

  headerBtn: {
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 6,
    border: "1px solid var(--gray-5, #363D39)",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.15s, color 0.15s",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },

  // Body (plot + optional panels)
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    // Mobile: column (panels go below); Desktop: row (panels on right)
    flexDirection: "column",
  },

  plotArea: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    position: "relative",
  },

  // Side panel container — on mobile renders below plot; on desktop to the right
  // Responsive handled via media queries embedded in a <style> tag in the component
  sidePanel: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxHeight: "50vh",
    overflowY: "auto",
    borderTop: "1px solid var(--gray-4, #2A302C)",
    background: "var(--gray-2, #1C211E)",
    flexShrink: 0,
  },

  sidePanelSection: {
    display: "flex",
    flexDirection: "column",
    borderBottom: "1px solid var(--gray-4, #2A302C)",
    minHeight: 0,
  },

  sidePanelSectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--gray-9, #6B7D72)",
    borderBottom: "1px solid var(--gray-3, #222827)",
    flexShrink: 0,
  },

  closePanelBtn: {
    padding: "0 4px",
    fontSize: 16,
    lineHeight: 1,
    border: "none",
    background: "none",
    color: "var(--gray-9, #6B7D72)",
    cursor: "pointer",
    fontFamily: "inherit",
    touchAction: "manipulation",
  },

  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    fontSize: 14,
    color: "var(--gray-8, #8A9590)",
  },

  // Flowfield sub-styles
  ffLoading: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
  },

  ffUnavailable: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 12px",
    minHeight: 40,
  },

  ffReady: {
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    overflowY: "auto",
    maxHeight: 160,
  },

  ffTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: "var(--gray-9, #6B7D72)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  ffPointList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },

  ffPoint: {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 0.12s, border-color 0.12s",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },

  ffVectorInfo: {
    padding: "4px 0",
    borderTop: "1px solid var(--gray-3, #222827)",
    marginTop: 4,
  },

  // Chat sub-styles
  chatLoading: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    flex: 1,
    minHeight: 60,
  },

  chatUnavailable: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    flex: 1,
    minHeight: 60,
  },

  chatPanel: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    maxHeight: "calc(50vh - 64px)",
  },

  chatMessages: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    // Smooth momentum scrolling on iOS
    WebkitOverflowScrolling: "touch",
  },

  chatEmpty: {
    fontSize: 12,
    color: "var(--gray-8, #8A9590)",
    textAlign: "center",
    padding: "8px 0",
  },

  chatBubble: {
    padding: "6px 10px",
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.4,
    maxWidth: "88%",
    wordBreak: "break-word",
  },

  chatBubbleUser: {
    background: "var(--accent-9, #00643C)",
    color: "#FFFFFF",
    alignSelf: "flex-end",
    borderBottomRightRadius: 3,
  },

  chatBubbleAssistant: {
    background: "var(--gray-3, #222827)",
    color: "var(--gray-12, #F0F2F0)",
    alignSelf: "flex-start",
    borderBottomLeftRadius: 3,
  },

  chatInputRow: {
    display: "flex",
    gap: 6,
    padding: "6px 8px",
    paddingBottom: "env(safe-area-inset-bottom, 6px)",
    borderTop: "1px solid var(--gray-4, #2A302C)",
    background: "var(--gray-2, #1C211E)",
    flexShrink: 0,
  },

  chatInput: {
    flex: 1,
    padding: "8px 10px",
    // 16px minimum prevents iOS Safari auto-zoom on focus
    fontSize: "max(16px, 14px)",
    border: "1px solid var(--gray-5, #363D39)",
    borderRadius: 8,
    background: "var(--gray-3, #222827)",
    color: "var(--gray-12, #F0F2F0)",
    fontFamily: "inherit",
    outline: "none",
  },

  chatSendBtn: {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    border: "none",
    borderRadius: 8,
    background: "var(--accent-9, #00643C)",
    color: "#FFFFFF",
    fontFamily: "inherit",
    flexShrink: 0,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    transition: "opacity 0.12s",
  },
};

// ---------------------------------------------------------------------------
// Responsive style injection — side panel goes to right column on wide screens
// ---------------------------------------------------------------------------

export function PlotShareViewerStyles() {
  return (
    <style>{`
      /* FXC-8724 PlotShareViewer responsive overrides */
      @media (min-width: 768px) {
        .psv-body {
          flex-direction: row !important;
        }
        .psv-side-panel {
          width: 320px !important;
          max-height: none !important;
          max-width: 320px !important;
          border-top: none !important;
          border-left: 1px solid var(--gray-4, #2A302C) !important;
          overflow-y: auto !important;
          flex-shrink: 0 !important;
        }
        .psv-chat-panel {
          max-height: none !important;
        }
      }

      @media (min-width: 1024px) {
        .psv-side-panel {
          width: 380px !important;
          max-width: 380px !important;
        }
      }

      /* Remove blue flash on tap (mobile) */
      .psv-btn {
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        user-select: none;
      }
    `}</style>
  );
}
